import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X, Monitor, Smartphone, Tablet, MapPin, Clock, Shield, Trash2 } from 'lucide-react'
import { format } from 'date-fns'
import toast from 'react-hot-toast'
import api from '../../utils/api'

const SessionsModal = ({ isOpen, onClose }) => {
  const [sessions, setSessions] = useState([])
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    if (isOpen) {
      fetchSessions()
    }
  }, [isOpen])

  const fetchSessions = async () => {
    setIsLoading(true)
    try {
      const response = await api.get('/admin/sessions')
      setSessions(response.data.sessions || [])
    } catch (error) {
      toast.error('Failed to fetch sessions')
      console.error('Failed to fetch sessions:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const revokeSession = async (sessionId) => {
    try {
      await api.delete(`/admin/sessions/${sessionId}`)
      setSessions(sessions.filter(session => session.id !== sessionId))
      toast.success('Session revoked successfully')
    } catch (error) {
      toast.error('Failed to revoke session')
    }
  }

  const getDeviceIcon = (deviceType) => {
    switch (deviceType?.toLowerCase()) {
      case 'mobile':
        return <Smartphone className="h-4 w-4" />
      case 'tablet':
        return <Tablet className="h-4 w-4" />
      default:
        return <Monitor className="h-4 w-4" />
    }
  }

  const getBrowserInfo = (userAgent) => {
    if (!userAgent) return 'Unknown Browser'
    
    if (userAgent.includes('Chrome')) return 'Chrome'
    if (userAgent.includes('Firefox')) return 'Firefox'
    if (userAgent.includes('Safari')) return 'Safari'
    if (userAgent.includes('Edge')) return 'Edge'
    return 'Unknown Browser'
  }

  const getLocationInfo = (session) => {
    if (session.city && session.country) {
      return `${session.city}, ${session.country}`
    }
    if (session.country) {
      return session.country
    }
    if (session.ip) {
      return session.ip
    }
    return 'Unknown Location'
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black bg-opacity-50"
              onClick={onClose}
            />
            
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-hidden"
            >
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <div className="flex items-center">
                  <div className="h-10 w-10 bg-primary-100 rounded-full flex items-center justify-center mr-3">
                    <Shield className="h-5 w-5 text-primary-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      Active Sessions
                    </h3>
                    <p className="text-sm text-gray-500">
                      Manage your active login sessions
                    </p>
                  </div>
                </div>
                <button
                  onClick={onClose}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* Content */}
              <div className="p-6 overflow-y-auto max-h-96">
                {isLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
                  </div>
                ) : sessions.length === 0 ? (
                  <div className="text-center py-8">
                    <Shield className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500">No active sessions found</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {sessions.map((session) => (
                      <div
                        key={session.id}
                        className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex items-start space-x-3">
                            <div className="h-10 w-10 bg-gray-100 rounded-full flex items-center justify-center">
                              {getDeviceIcon(session.deviceType)}
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center space-x-2">
                                <h4 className="text-sm font-medium text-gray-900">
                                  {getBrowserInfo(session.userAgent)}
                                </h4>
                                {session.isCurrent && (
                                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                    Current Session
                                  </span>
                                )}
                              </div>
                              
                              <div className="mt-1 space-y-1">
                                <div className="flex items-center text-xs text-gray-500">
                                  <MapPin className="h-3 w-3 mr-1" />
                                  {getLocationInfo(session)}
                                </div>
                                <div className="flex items-center text-xs text-gray-500">
                                  <Clock className="h-3 w-3 mr-1" />
                                  Last active: {format(new Date(session.lastActivity), 'MMM d, yyyy h:mm a')}
                                </div>
                                {session.createdAt && (
                                  <div className="flex items-center text-xs text-gray-500">
                                    <Shield className="h-3 w-3 mr-1" />
                                    Signed in: {format(new Date(session.createdAt), 'MMM d, yyyy h:mm a')}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                          
                          {!session.isCurrent && (
                            <button
                              onClick={() => revokeSession(session.id)}
                              className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              title="Revoke session"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="border-t border-gray-200 p-6">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-500">
                    {sessions.length} active session{sessions.length !== 1 ? 's' : ''}
                  </div>
                  <div className="flex space-x-3">
                    <button
                      onClick={fetchSessions}
                      className="btn btn-secondary"
                      disabled={isLoading}
                    >
                      Refresh
                    </button>
                    <button
                      onClick={onClose}
                      className="btn btn-primary"
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      )}
    </AnimatePresence>
  )
}

export default SessionsModal